var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { ListItemIcon } from '@mui/material';
import { Check16 } from '@yunex/yds-icons';
import clsx from 'clsx';
import React, { cloneElement, createRef, useState } from 'react';
import { resolveSxAsArray } from '../../../utils/utils';
import Checkbox from '../../Checkbox';
import { LabelPlacement } from '../../Form/FormInputProps';
import MenuFilterInputItem from '../../MenuFilterInputItem';
import MenuItem from '../../MenuItem';
import MenuItemText from '../../MenuItemText';
import NestedMenuItem from '../../NestedMenuItem';
import { rem } from '../../../utils/rem';
/* ********************************************************************************************* */
/* This utility handles all components' transformations into menu items.                         */
/* ********************************************************************************************* */
// Constants.
var CHECKBOX_STYLES = {
    ml: 0,
};
var styles = {
    hoverOffChipStyles: {
        '&:hover': {
            backgroundColor: 'transparent',
            cursor: 'default',
        },
    },
};
// Helper functions.
var isMenuItem = function (el) { var _a; return ((_a = el.type) === null || _a === void 0 ? void 0 : _a.displayName) === 'MenuItem'; };
/**
 * Returns the selected item icon either specified by user or the default one.
 *
 * @param item The wrapper containing all data.
 * @returns The selected item icon.
 */
var getSelectedItemIcon = function (item) { var _a, _b; return (_b = (_a = item.MenuItemProps) === null || _a === void 0 ? void 0 : _a.selectedItemIcon) !== null && _b !== void 0 ? _b : React.createElement(Check16, null); };
// Transformation functions.
/**
 * Function to transform the specified `Button` component into the clickable `MenuItem` component.
 *
 * @param item The wrapper containing a `Button` component with additional meta data.
 * @param handleClose The function to handle closing the menu on click.
 * @param isWrapped Determines whether this component is wrapped in another component (typically Tooltip).
 * @returns {MenuItem} A `MenuItem` component with the transformed `Button` component.
 */
export var TransformedButton = function (_a) {
    var _b, _c, _d, _e, _f;
    var item = _a.item, handleClose = _a.handleClose, isWrapped = _a.isWrapped;
    var cmp = (_d = (isWrapped ? (_c = (_b = item.el) === null || _b === void 0 ? void 0 : _b.props) === null || _c === void 0 ? void 0 : _c.children : item.el).props) !== null && _d !== void 0 ? _d : {};
    var getItemLabel = function () {
        var _a;
        var lbl = item.label || item.el.props.children;
        if (isWrapped && typeof lbl !== 'string') {
            lbl = (_a = lbl === null || lbl === void 0 ? void 0 : lbl.props) === null || _a === void 0 ? void 0 : _a.children;
        }
        return lbl;
    };
    return (React.createElement(MenuItem, __assign({ key: ((_e = item.el) === null || _e === void 0 ? void 0 : _e.key) || getItemLabel(), "data-testid": cmp['data-testid'] }, item.MenuItemProps, { onClick: function (event) {
            var _a, _b, _c;
            (_a = cmp.onClick) === null || _a === void 0 ? void 0 : _a.call(cmp, event);
            handleClose();
            (_c = (_b = item.MenuItemProps) === null || _b === void 0 ? void 0 : _b.onClick) === null || _c === void 0 ? void 0 : _c.call(_b, event);
        }, danger: cmp.danger, disabled: cmp.disabled }),
        cmp.startIcon && React.createElement(ListItemIcon, null, cmp.startIcon),
        cmp.icon && React.createElement(ListItemIcon, null, cmp.children),
        React.createElement(MenuItemText, { primaryTypographyProps: { noWrap: true }, inset: (_f = item.MenuItemProps) === null || _f === void 0 ? void 0 : _f.inset }, getItemLabel())));
};
/**
 * Function to transform the specified `Chip` component into a clickable `MenuItem` component with a chip.
 * Note that the whole menu item is clickable, not only the chip.
 *
 * @param item The wrapper containing a `Chip` component with additional meta data.
 * @param isWrapped Determines whether this component is wrapped in another component (typically Tooltip).
 * @returns {MenuItem} A `MenuItem` component with the cloned `Chip` component.
 */
export var TransformChip = function (_a) {
    var _b, _c, _d, _e, _f;
    var item = _a.item, isWrapped = _a.isWrapped;
    var cmp = (_d = (isWrapped ? (_c = (_b = item.el) === null || _b === void 0 ? void 0 : _b.props) === null || _c === void 0 ? void 0 : _c.children : item.el).props) !== null && _d !== void 0 ? _d : {};
    var handleClick = function (e) {
        var _a, _b, _c;
        (_b = (_a = item.MenuItemProps) === null || _a === void 0 ? void 0 : _a.onClick) === null || _b === void 0 ? void 0 : _b.call(_a, e);
        // Condition to prevent double onClick event.
        if (e.target.tagName === 'LI') {
            (_c = cmp.onClick) === null || _c === void 0 ? void 0 : _c.call(cmp, e);
        }
    };
    var handleKeyDown = function (e) {
        var _a, _b, _c;
        (_b = (_a = item.MenuItemProps) === null || _a === void 0 ? void 0 : _a.onKeyDown) === null || _b === void 0 ? void 0 : _b.call(_a, e);
        if (e.key === 'Backspace' || e.key === 'Delete') {
            (_c = cmp.onDelete) === null || _c === void 0 ? void 0 : _c.call(cmp, e);
        }
    };
    return (React.createElement(MenuItem
    // Directly provided key should be used if available because labels can be same.
    , __assign({ "data-testid": cmp['data-testid'], disabled: cmp.disabled }, item.MenuItemProps, { key: (item === null || item === void 0 ? void 0 : item.key) || ((_e = item.el) === null || _e === void 0 ? void 0 : _e.key) || cmp.label || item.label, sx: __spreadArray([
            !cmp.onClick && styles.hoverOffChipStyles
        ], resolveSxAsArray((_f = item.MenuItemProps) === null || _f === void 0 ? void 0 : _f.sx), true), onClick: handleClick, onKeyDown: handleKeyDown }), item.el));
};
/**
 * Function to transform the specified `Checkbox` component into the `MenuItem` component.
 *
 * @param item The wrapper containing a `Checkbox` component with additional meta data.
 * @returns {MenuItem} A `MenuItem` component with the transformed `Checkbox` component and a label.
 */
export var TransformCheckbox = function (_a) {
    var _b, _c, _d, _e, _f, _g;
    var item = _a.item;
    var props = ((_d = (_c = (_b = item.el) === null || _b === void 0 ? void 0 : _b.props) === null || _c === void 0 ? void 0 : _c.control) === null || _d === void 0 ? void 0 : _d.props) || ((_e = item.el) === null || _e === void 0 ? void 0 : _e.props) || {};
    var label = ((_g = (_f = item.el) === null || _f === void 0 ? void 0 : _f.props) === null || _g === void 0 ? void 0 : _g.label) || item.label;
    var handleClick = function (e) {
        var _a, _b;
        props === null || props === void 0 ? void 0 : props.onChange(e);
        (_b = (_a = item === null || item === void 0 ? void 0 : item.MenuItemProps) === null || _a === void 0 ? void 0 : _a.onClick) === null || _b === void 0 ? void 0 : _b.call(_a, e);
    };
    return (React.createElement(MenuItem, __assign({ disabled: props === null || props === void 0 ? void 0 : props.disabled, "data-testid": props['data-testid'] }, item.MenuItemProps, { key: props.value || label, onClick: handleClick }),
        React.createElement(ListItemIcon, null,
            React.createElement(Checkbox, __assign({}, props, { onChange: null, sx: CHECKBOX_STYLES }))),
        React.createElement(MenuItemText, { primaryTypographyProps: { noWrap: true } }, label)));
};
var DropdownButtonMenuItem = function (_a) {
    var _b, _c, _d;
    var menuItem = _a.menuItem, item = _a.item, handleClose = _a.handleClose;
    var _e = menuItem.props || {}, disabled = _e.disabled, onClick = _e.onClick, value = _e.value;
    var handleMenuItemClick = function (event) {
        var _a, _b;
        onClick === null || onClick === void 0 ? void 0 : onClick(event);
        handleClose();
        (_b = (_a = item === null || item === void 0 ? void 0 : item.MenuItemProps) === null || _a === void 0 ? void 0 : _a.onClick) === null || _b === void 0 ? void 0 : _b.call(_a, event);
    };
    var menuTestId = (_b = menuItem.props) === null || _b === void 0 ? void 0 : _b['data-testid'];
    return (React.createElement(MenuItem, __assign({ "data-testid": menuTestId, disabled: disabled }, item.MenuItemProps, { key: menuItem.key || ((_c = menuItem.props) === null || _c === void 0 ? void 0 : _c.children), onClick: handleMenuItemClick, value: value }), (_d = menuItem.props) === null || _d === void 0 ? void 0 : _d.children));
};
/**
 * Function to transform the specified dropdown `Button` component into the `NestedMenuItem` component with submenu items
 * defined in the dropdown menu.
 *
 * @param item The wrapper containing a dropdown `Button` component with additional meta data.
 * @param handleClose Function to handle closing the menu on click and keyboard event (e.g. ENTER, SPACE).
 * @returns {NestedMenuItem} A `NestedMenuItem` component with subitems defined in the dropdown menu.
 */
export var TransformDropdownButton = function (_a) {
    var _b, _c, _d, _e, _f, _g, _h, _j, _k;
    var item = _a.item, handleClose = _a.handleClose;
    var btnProps = (_d = (_c = (_b = item.el) === null || _b === void 0 ? void 0 : _b.props) === null || _c === void 0 ? void 0 : _c.children[0]) === null || _d === void 0 ? void 0 : _d.props;
    var label = item.label || (btnProps === null || btnProps === void 0 ? void 0 : btnProps.children);
    var dropdownMenuItems = (_h = (_g = (_f = (_e = item.el) === null || _e === void 0 ? void 0 : _e.props) === null || _f === void 0 ? void 0 : _f.children[1]) === null || _g === void 0 ? void 0 : _g.props) === null || _h === void 0 ? void 0 : _h.children;
    return (React.createElement(NestedMenuItem, __assign({ "data-testid": btnProps === null || btnProps === void 0 ? void 0 : btnProps['data-testid'], disabled: (_j = item.el) === null || _j === void 0 ? void 0 : _j.props.disabled, key: ((_k = item.el) === null || _k === void 0 ? void 0 : _k.key) || label, label: label, parentMenuOpen: true }, item.NestedMenuItemProps), dropdownMenuItems.map(function (menuItem, index) {
        if (isMenuItem(menuItem)) {
            return (React.createElement(DropdownButtonMenuItem, { key: index, item: item, menuItem: menuItem, handleClose: handleClose }));
        }
        else {
            return menuItem;
        }
    })));
};
/**
 * Function to transform the specified variant of `FormField` component (i.e. either Text or Date/TimePicker)
 * into the `MenuItem` component.
 *
 * @param item The wrapper containing a component with additional meta data.
 * @param isDatePicker Determines whether the `FormField` component is a `DatePicker` or `DateTimePicker`.
 * @returns {MenuItem} A `MenuItem` component with the specified `FormField` component.
 */
export var TransformFormField = function (_a) {
    var _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
    var item = _a.item, isDatePicker = _a.isDatePicker;
    var ref = createRef();
    var inputRef = createRef();
    var handleClick = function (e) {
        var _a, _b, _c, _d;
        // Request focus to the input element.
        (_a = ref === null || ref === void 0 ? void 0 : ref.current) === null || _a === void 0 ? void 0 : _a.focus();
        (_b = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) === null || _b === void 0 ? void 0 : _b.focus();
        (_d = (_c = item.MenuItemProps) === null || _c === void 0 ? void 0 : _c.onClick) === null || _d === void 0 ? void 0 : _d.call(_c, e);
    };
    var slotProps = isDatePicker
        ? {
            slotProps: __assign(__assign({}, (_c = (_b = item.el) === null || _b === void 0 ? void 0 : _b.props) === null || _c === void 0 ? void 0 : _c.slotProps), { field: __assign(__assign({}, (_f = (_e = (_d = item.el) === null || _d === void 0 ? void 0 : _d.props) === null || _e === void 0 ? void 0 : _e.slotProps) === null || _f === void 0 ? void 0 : _f.field), { labelPlacement: LabelPlacement.TOP, onKeyDown: function (e) {
                        var _a, _b, _c, _d, _e;
                        if (e.key !== 'Escape') {
                            e.stopPropagation();
                        }
                        (_e = (_d = (_c = (_b = (_a = item.el) === null || _a === void 0 ? void 0 : _a.props) === null || _b === void 0 ? void 0 : _b.slotProps) === null || _c === void 0 ? void 0 : _c.field) === null || _d === void 0 ? void 0 : _d.onKeyDown) === null || _e === void 0 ? void 0 : _e.call(_d, e);
                    } }) }),
        }
        : {};
    return (React.createElement(MenuItem, __assign({ "data-testid": (_h = (_g = item.el) === null || _g === void 0 ? void 0 : _g.props) === null || _h === void 0 ? void 0 : _h['data-testid'], disabled: (_k = (_j = item.el) === null || _j === void 0 ? void 0 : _j.props) === null || _k === void 0 ? void 0 : _k.disabled }, item.MenuItemProps, { key: ((_l = item.el) === null || _l === void 0 ? void 0 : _l.key) || (item === null || item === void 0 ? void 0 : item.label) || ((_o = (_m = item.el) === null || _m === void 0 ? void 0 : _m.props) === null || _o === void 0 ? void 0 : _o.label), onClick: handleClick, sx: __assign({ height: 'auto' }, resolveSxAsArray((_p = item === null || item === void 0 ? void 0 : item.MenuItemProps) === null || _p === void 0 ? void 0 : _p.sx)) }), cloneElement(item.el, __assign({ labelPlacement: LabelPlacement.TOP, inputProps: {
            ref: inputRef,
        }, inputRef: isDatePicker ? inputRef : undefined, ref: isDatePicker ? undefined : ref, style: {
            maxWidth: '100%',
            minWidth: 0,
        } }, slotProps))));
};
var PlaceholderMenuItem = function (_a) {
    var cmp = _a.cmp, item = _a.item;
    var onChange = cmp.onChange, value = cmp.value, placeholderText = cmp.placeholderText;
    return (React.createElement(MenuItem, { color: "hint", onClick: onChange, value: "" },
        React.createElement(ListItemIcon, { sx: {
                visibility: value ? 'hidden' : 'visible',
            } }, getSelectedItemIcon(item)),
        React.createElement("i", null, placeholderText)));
};
/**
 * Function to transform the specified `Select` component into the `NestedMenuItem` component, i.e. into a menu item with submenu.
 *
 * @param item The wrapper containing a `Select` or 'SelectFormInput` components with additional meta data.
 * @returns {NestedMenuItem} A `NestedMenuItem` component with items transformed from the `Select`.
 */
export var TransformSelect = function (_a) {
    var _b, _c, _d, _e;
    var item = _a.item;
    var cmp = (_d = (_c = (_b = item.el) === null || _b === void 0 ? void 0 : _b.props) !== null && _c !== void 0 ? _c : item) !== null && _d !== void 0 ? _d : {};
    var items = cmp.children || cmp.items;
    var nestedMenuRef = createRef();
    var _f = useState(''), searchFieldValue = _f[0], setSearchFieldValue = _f[1];
    // Utils functions.
    /**
     * Converts the specified value to the format understood by the `Select` component. For `multiple` selection
     * it simply returns the `value`, otherwise it returns a list of current selection.
     */
    var convertValue = function (value) {
        var retVal;
        if (cmp.multiple) {
            var index = cmp.value.indexOf(value);
            if (index >= 0) {
                retVal = cmp.value.slice();
                retVal.splice(index, 1);
            }
            else {
                retVal = cmp.value.concat(value);
            }
        }
        else {
            retVal = value;
        }
        return retVal;
    };
    var handleChange = function (event, value) {
        var _a;
        (_a = cmp.onChange) === null || _a === void 0 ? void 0 : _a.call(cmp, event, convertValue(value));
    };
    var isFilterableItem = function (el) { var _a; return isMenuItem(el) && ((_a = el === null || el === void 0 ? void 0 : el.props) === null || _a === void 0 ? void 0 : _a.filterable); };
    var isItemSelected = function (value) {
        return cmp.multiple ? cmp.value.includes(value) : (cmp.value || '') === value;
    };
    var getFilteredItems = function () {
        // Filter out items which can be used for filtering.
        var filterableItems = items.filter(isFilterableItem);
        // Filter out items which match the searched value.
        return filterableItems.filter(function (fi) {
            return fi.props.children.toLowerCase().indexOf(searchFieldValue.trim().toLowerCase()) !==
                -1;
        });
    };
    var createMenuItem = function (mi, isFilterable) {
        var _a, _b;
        if (isFilterable === void 0) { isFilterable = true; }
        return (React.createElement(MenuItem, __assign({ "data-testid": (_a = mi.props) === null || _a === void 0 ? void 0 : _a['data-testid'], disabled: mi.props.disabled }, item.MenuItemProps, { key: mi.key || ((_b = mi.props) === null || _b === void 0 ? void 0 : _b.value), onClick: function (event) {
                var _a, _b, _c, _d;
                (_b = (_a = mi.props).onClick) === null || _b === void 0 ? void 0 : _b.call(_a, event);
                handleChange(event, mi.props.value);
                (_d = (_c = item.MenuItemProps) === null || _c === void 0 ? void 0 : _c.onClick) === null || _d === void 0 ? void 0 : _d.call(_c, event);
            }, value: mi.props.value }),
            cmp.multiple && isFilterable ? (React.createElement(ListItemIcon, null,
                React.createElement(Checkbox, { checked: isItemSelected(mi.props.value), sx: CHECKBOX_STYLES }))) : (React.createElement(ListItemIcon, { sx: {
                    visibility: isItemSelected(mi.props.value) ? 'visible' : 'hidden',
                } }, getSelectedItemIcon(item))),
            mi.props.children));
    };
    var label = (item === null || item === void 0 ? void 0 : item.label) || cmp.label;
    return (React.createElement(NestedMenuItem, __assign({ "data-testid": cmp['data-testid'], hideSubmenuOnItemClick: !cmp.multiple, key: ((_e = item.el) === null || _e === void 0 ? void 0 : _e.key) || label, label: label, parentMenuOpen: true, ref: nestedMenuRef }, item.NestedMenuItemProps),
        item.filterable && (React.createElement(MenuFilterInputItem, { filterPlaceholderText: item.filterPlaceholderText, nestedMenuRef: nestedMenuRef, searchFieldValue: searchFieldValue, setSearchFieldValue: setSearchFieldValue, textFieldSx: {
                width: rem(150),
                flex: 1,
            } })),
        cmp.displayEmpty !== false && !cmp.multiple && (React.createElement(PlaceholderMenuItem, { cmp: cmp, item: item })),
        item.filterable && getFilteredItems().map(function (mi) { return createMenuItem(mi); }),
        items
            .filter(function (it) { return !isFilterableItem(it); })
            .map(function (mi) {
            return isMenuItem(mi) ? createMenuItem(mi, !item.filterable) : mi;
        })));
};
/**
 * Function to transform the specified `Divider` component into the horizontal separator.
 *
 * @param item The wrapper containing a `Divider` component.
 * @param index Index of the item in the list. Used to generate a unique key if key is not provided.
 * @returns A cloned `Divider` component with horizontal orientation.
 */
export var TransformSeparator = function (_a) {
    var _b;
    var item = _a.item, index = _a.index;
    return cloneElement(item.el, {
        orientation: 'horizontal',
        flexItem: false,
        key: ((_b = item.el) === null || _b === void 0 ? void 0 : _b.key) || 'separator' + index,
    });
};
/**
 * Function to transform the specified `TextField` component into the `MenuItem` component.
 *
 * @param item The wrapper containing a `TextField` component with additional meta data.
 * @returns {MenuItem} A `MenuItem` component with the specified `TextField` component.
 */
export var TransformTextField = function (_a) {
    var _b, _c, _d, _e, _f, _g, _h, _j, _k;
    var item = _a.item;
    var ref = createRef();
    var inputRef = createRef();
    return (React.createElement(MenuItem, __assign({ disabled: (_c = (_b = item.el) === null || _b === void 0 ? void 0 : _b.props) === null || _c === void 0 ? void 0 : _c.disabled, "data-testid": (_e = (_d = item.el) === null || _d === void 0 ? void 0 : _d.props) === null || _e === void 0 ? void 0 : _e['data-testid'] }, item.MenuItemProps, { key: ((_f = item.el) === null || _f === void 0 ? void 0 : _f.key) ||
            (item === null || item === void 0 ? void 0 : item.label) ||
            ((_h = (_g = item.el) === null || _g === void 0 ? void 0 : _g.props) === null || _h === void 0 ? void 0 : _h.label) ||
            ((_j = item.el.props) === null || _j === void 0 ? void 0 : _j.placeholder) ||
            'TextFieldKey', onClick: function (e) {
            var _a, _b, _c, _d;
            // Request focus to the input element.
            (_a = ref === null || ref === void 0 ? void 0 : ref.current) === null || _a === void 0 ? void 0 : _a.focus();
            (_b = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) === null || _b === void 0 ? void 0 : _b.focus();
            (_d = (_c = item.MenuItemProps) === null || _c === void 0 ? void 0 : _c.onClick) === null || _d === void 0 ? void 0 : _d.call(_c, e);
        }, sx: __assign({ height: 'auto' }, resolveSxAsArray((_k = item === null || item === void 0 ? void 0 : item.MenuItemProps) === null || _k === void 0 ? void 0 : _k.sx)) }), cloneElement(item.el, {
        inputRef: ref,
        inputProps: {
            ref: inputRef,
        },
        style: {
            maxWidth: '100%',
            minWidth: 0,
        },
    })));
};
/**
 * Function to transform the specified `ToggleButton` component into the `MenuItem` component with a leading checkbox.
 *
 * @param item The wrapper containing a `ToggleButton` component with additional meta data.
 * @returns {MenuItem} A `MenuItem` component with a `Checkbox` component and a label.
 */
export var TransformToggleButton = function (_a) {
    var _b, _c, _d, _e;
    var item = _a.item;
    var cmp = (_c = (_b = item.el) === null || _b === void 0 ? void 0 : _b.props) !== null && _c !== void 0 ? _c : {};
    var _f = (_d = item.MenuItemProps) !== null && _d !== void 0 ? _d : {}, selectedItemIcon = _f.selectedItemIcon, deselectedItemIcon = _f.deselectedItemIcon, otherMenuItemProps = __rest(_f, ["selectedItemIcon", "deselectedItemIcon"]);
    /**
     * Returns either a custom icon for the selected state, or the one for the deselected state (if specified),
     * or the default checkbox.
     */
    var resolveIcon = function () {
        var _a;
        var icon;
        if (!((_a = item.MenuItemProps) === null || _a === void 0 ? void 0 : _a.selectedItemIcon)) {
            icon = React.createElement(Checkbox, { checked: cmp.value, sx: CHECKBOX_STYLES });
        }
        else if (cmp.value) {
            icon = selectedItemIcon;
        }
        else {
            icon = deselectedItemIcon;
        }
        return icon;
    };
    return (React.createElement(MenuItem, __assign({ disabled: cmp.disabled, "data-testid": cmp['data-testid'] }, otherMenuItemProps, { key: ((_e = item.el) === null || _e === void 0 ? void 0 : _e.key) || item.label || cmp.children, onClick: cmp.onChange }),
        React.createElement(ListItemIcon, null, resolveIcon()),
        React.createElement(MenuItemText, { primaryTypographyProps: { noWrap: true } }, item.label || cmp.children)));
};
/**
 * Function to transform the specified `ToggleButtonGroup` or `ButtonGroup` component into the `NestedMenuItem` component, i.e. into a menu item with submenu.
 * ToggleButtonGroup: For the exclusive selection clicking on any submenu item will close the submenu, otherwise it keeps open.
 * ButtonGroup: Clicking on item will close the menu.
 *
 * @param item The wrapper containing a `ToggleButtonGroup` or `ButtonGroup` component with additional meta data.
 * @param handleClose The function to close the menu.
 * @param isToggleGroup Flag indicating whether the specified component is `ToggleButtonGroup` or `ButtonGroup`.
 * @returns {NestedMenuItem} A `NestedMenuItem` component with items transformed from the `ToggleButtonGroup` or `ButtonGroup`.
 */
export var TransformButtonGroup = function (_a) {
    var _b, _c;
    var item = _a.item, handleClose = _a.handleClose, isToggleGroup = _a.isToggleGroup;
    var cmp = (_c = (_b = item.el) === null || _b === void 0 ? void 0 : _b.props) !== null && _c !== void 0 ? _c : {};
    /**
     * Converts the specified value to the format understood by the `ToggleButtonGroup` component. For `exclusive` selection
     * it simply returns either the `value` (for selection) or `null` (for deselection), otherwise it returns a list of
     * current selection.
     */
    var convertValue = function (value) {
        var _a;
        var retVal;
        if (cmp.exclusive) {
            retVal = value === cmp.value ? null : value;
        }
        else {
            var index = (_a = cmp.value) === null || _a === void 0 ? void 0 : _a.indexOf(value);
            if (cmp.value && index >= 0) {
                retVal = cmp.value.slice();
                retVal.splice(index, 1);
            }
            else {
                retVal = cmp.value ? cmp.value.concat(value) : [value];
            }
        }
        return retVal;
    };
    var handleChange = function (event, value) {
        var _a;
        (_a = cmp.onChange) === null || _a === void 0 ? void 0 : _a.call(cmp, event, convertValue(value));
    };
    return (React.createElement(NestedMenuItem, __assign({ "data-testid": cmp['data-testid'], hideSubmenuOnItemClick: Boolean(cmp.exclusive) || !isToggleGroup, key: item.label, label: item.label, parentMenuOpen: true }, item.NestedMenuItemProps), cmp.children.map(function (option) {
        var _a;
        return (React.createElement(MenuItem, __assign({ "data-testid": (_a = option.props) === null || _a === void 0 ? void 0 : _a['data-testid'], disabled: option.props.disabled }, item.MenuItemProps, { key: option.key || option.props.value, onClick: function (event) {
                var _a, _b;
                handleChange(event, option.props.value);
                (_b = (_a = item.MenuItemProps) === null || _a === void 0 ? void 0 : _a.onClick) === null || _b === void 0 ? void 0 : _b.call(_a, event);
                if (!isToggleGroup) {
                    // This will close whole action group.
                    handleClose();
                }
            } }),
            isToggleGroup &&
                (cmp.exclusive ? (React.createElement(ListItemIcon, { sx: {
                        visibility: cmp.value === option.props.value ? 'visible' : 'hidden',
                    } }, getSelectedItemIcon(item))) : (React.createElement(ListItemIcon, null,
                    React.createElement(Checkbox, { checked: cmp.value.includes(option.props.value), sx: CHECKBOX_STYLES })))),
            option.props.children));
    })));
};
/**
 * Function to transform the specified `TopNavItem` component into the clickable `MenuItem` component.
 *
 * @param item The wrapper containing a `TopNavItem` component with additional meta data.
 * @param handleClose The function to handle closing the menu on click.
 * @param isWrapped Determines whether this component is wrapped in another component (typically Tooltip).
 * @returns {MenuItem} A `MenuItem` component with the transformed `TopNavItem` component.
 */
export var TransformTopNavItem = function (_a) {
    var _b, _c, _d, _e, _f, _g;
    var item = _a.item, handleClose = _a.handleClose, isWrapped = _a.isWrapped;
    var cmp = (_d = (isWrapped ? (_c = (_b = item.el) === null || _b === void 0 ? void 0 : _b.props) === null || _c === void 0 ? void 0 : _c.children : item.el).props) !== null && _d !== void 0 ? _d : {};
    var getItemLabel = function () {
        var lbl = item.label || item.el.props.children;
        if (isWrapped) {
            lbl = lbl.props.children;
        }
        return lbl;
    };
    return (React.createElement(MenuItem, __assign({ key: ((_e = item.el) === null || _e === void 0 ? void 0 : _e.key) || getItemLabel(), "data-testid": cmp['data-testid'] }, item.MenuItemProps, { onClick: function (event) {
            var _a;
            (_a = cmp.onClick) === null || _a === void 0 ? void 0 : _a.call(cmp, event);
            handleClose();
        }, danger: cmp.danger, disabled: cmp.disabled, selected: cmp.selected, to: cmp.to, className: clsx(cmp.className, (_f = cmp.selected) !== null && _f !== void 0 ? _f : cmp === null || cmp === void 0 ? void 0 : cmp.activeClassName) }),
        cmp.startIcon && React.createElement(ListItemIcon, null, cmp.startIcon),
        cmp.icon && React.createElement(ListItemIcon, null, cmp.children),
        React.createElement(MenuItemText, { primaryTypographyProps: { noWrap: true }, inset: (_g = item.MenuItemProps) === null || _g === void 0 ? void 0 : _g.inset }, getItemLabel())));
};
